import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { money, pct, relativeTime } from "../lib/api";
import PageHeader, { StatusPill } from "../components/app/PageHeader";
import { Plus, ArrowRight, TrendingUp, FileText, CheckCircle2, Edit3, Sparkles } from "lucide-react";

function StatBlock({ label, value, sub, accent, icon: Icon }) {
  return (
    <div className="surface p-6 relative overflow-hidden">
      {accent && (
        <div
          className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-30 blur-2xl"
          style={{ background: "var(--gradient-accent)" }}
        />
      )}
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow">{label}</div>
          {Icon && (
            <div
              className="p-1.5 rounded-md"
              style={{
                background: accent ? "var(--gradient-accent-soft)" : "var(--surface-2)",
                color: accent ? "var(--accent)" : "var(--text-muted)",
              }}
            >
              <Icon className="w-3.5 h-3.5" />
            </div>
          )}
        </div>
        <div
          className={`font-display text-3xl font-bold tracking-tight ${accent ? "text-gradient" : ""}`}
          style={!accent ? { color: "var(--text-primary)" } : {}}
          data-num
        >
          {value}
        </div>
        {sub && (
          <div className="text-xs font-mono mt-2" style={{ color: "var(--text-muted)" }}>{sub}</div>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/dashboard/recent-quotes", { params: { limit: 10 } }).then((r) => setRecent(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Mission Control"
        title="Dashboard"
        subtitle="What's quoted, what's won, what's still in flight."
        actions={
          <button onClick={() => nav("/quotes/new")} data-testid="dashboard-new-quote-btn" className="btn btn-primary">
            <Plus className="w-4 h-4" /> New Quote
          </button>
        }
      />

      <div className="p-10 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <StatBlock
            label="Quotes This Month"
            value={stats ? stats.quotes_this_month : "—"}
            sub={stats ? `${money(stats.value_this_month)} pipeline` : ""}
            icon={Edit3}
          />
          <StatBlock
            label="Approved Value (YTD)"
            value={stats ? money(stats.approved_value) : "—"}
            sub={stats ? `${stats.approved} approved` : ""}
            accent
            icon={TrendingUp}
          />
          <StatBlock
            label="Drafts"
            value={stats ? stats.drafts : "—"}
            sub="In progress"
            icon={FileText}
          />
          <StatBlock
            label="Active Clients"
            value={stats ? stats.client_count : "—"}
            sub={stats ? `${stats.quote_count} total quotes` : ""}
            icon={CheckCircle2}
          />
        </div>

        {/* Recent quotes */}
        <div className="surface overflow-hidden">
          <div className="flex items-center justify-between px-6 py-5 border-b" style={{ borderColor: "var(--border-subtle)" }}>
            <div>
              <div className="label-eyebrow mb-1">Activity Feed</div>
              <h2 className="font-display text-xl font-bold tracking-tight" style={{ color: "var(--text-primary)" }}>
                Recent Quotes
              </h2>
            </div>
            <Link
              to="/quotes"
              data-testid="dashboard-view-all-quotes"
              className="btn btn-ghost btn-sm"
            >
              View All <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Quote #</th>
                <th>Title</th>
                <th>Client</th>
                <th>Status</th>
                <th className="text-right">Total</th>
                <th className="text-right">Margin</th>
                <th className="text-right">Updated</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center py-20">
                    <div className="flex flex-col items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center"
                        style={{ background: "var(--surface-2)" }}
                      >
                        <Sparkles className="w-5 h-5" style={{ color: "var(--accent)" }} />
                      </div>
                      <div className="text-sm" style={{ color: "var(--text-muted)" }}>
                        No quotes yet — let's launch your first one.
                      </div>
                      <Link to="/quotes/new" className="btn btn-primary btn-sm">
                        <Plus className="w-3.5 h-3.5" /> Create Quote
                      </Link>
                    </div>
                  </td>
                </tr>
              )}
              {recent.map((q) => (
                <tr
                  key={q.id}
                  className="clickable"
                  onClick={() => nav(`/quotes/${q.id}`)}
                  data-testid={`recent-quote-row-${q.id}`}
                >
                  <td className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>{q.quote_number}</td>
                  <td className="font-medium">{q.title || "—"}</td>
                  <td style={{ color: "var(--text-secondary)" }}>{q.client_name || "—"}</td>
                  <td><StatusPill status={q.status} /></td>
                  <td className="text-right font-mono" data-num>{money(q.total)}</td>
                  <td className="text-right font-mono" style={{ color: "var(--text-secondary)" }} data-num>{pct(q.margin_pct)}</td>
                  <td className="text-right text-xs font-mono" style={{ color: "var(--text-faint)" }}>{relativeTime(q.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
